import React from 'react';
import { Button, Link } from '@americanexpress/dls-react';
import { useRouteParams } from '@/context/routeParamsContext';
import {
  centurionLoungeEligibleCards,
  waitlistStatus,
  WAITLIST_SCROLL_POSITION_KEY,
} from '@/constants/waitlistLounges';
import { useSelectedProductFromUserWallet } from '@/hooks/memberHooks';
import { useUserWaitListDetails } from '@/hooks/waitListHooks/waitListHelperHooks';
import { locales } from '@/constants/supportedLocales';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { getIsLoggedIn } from '@/selectors/authSelectors';
import { useAnalyticsContext } from '@/context/analyticsContext';
import { useModal } from '@/context/modalContext';
import { saveScrollBeforeExternalNavigation } from '@/helpers/utils';
import styles from './WaitListButton.module.scss';
import { modalTypes } from '../common/modals/constants';

export default function WaitListButton() {
  const { customTrack } = useAnalyticsContext();
  const { locale, loungeId, product } = useRouteParams();
  const { buttonText, Icon, instructions, prefix, status, title, url } =
    useUserWaitListDetails(loungeId) || {};
  const isLoggedIn = useSelector(getIsLoggedIn);
  // retrieve product type if product in view is in CM's wallet
  const { product: { type: productTypes = [] } = {}, homeCountryLocale } =
    useSelectedProductFromUserWallet(product, locale) || {};
  const { setModalName } = useModal();

  // use card to determine locale when logged in
  const isUSProduct = isLoggedIn
    ? homeCountryLocale === locales.UnitedStates
    : locale === locales.UnitedStates.toLocaleLowerCase();
  const userHasEligibleProduct = centurionLoungeEligibleCards.some(
    (eligibleCard) => productTypes.includes(eligibleCard) && isUSProduct
  );
  const shouldHideWaitlistLink = !buttonText || !userHasEligibleProduct;

  // Display link for only US products
  const shouldHideLink = isLoggedIn ? shouldHideWaitlistLink : !isUSProduct;

  if (shouldHideLink) {
    return null;
  }

  const loginStateText = isLoggedIn ? 'logged-in' : 'logged-out';

  const clickHandler = (event) => {
    customTrack({
      event,
      detail: `${buttonText}`,
      element: `${loginStateText} WaitList Button`,
    });

    saveScrollBeforeExternalNavigation({
      storageKey: WAITLIST_SCROLL_POSITION_KEY,
    });
  };

  const shouldOpenModal = [waitlistStatus.GET, waitlistStatus.JOIN].includes(
    status
  );
  const displayEntryPoint = () => {
    if (shouldOpenModal) {
      return (
        <Button
          className="tw-w-full tw-max-w-300"
          variant="primary"
          onClick={(e) => {
            setModalName(modalTypes.BEFORE_YOU_GO_CONFIRMATION_MODAL);
            clickHandler(e);
          }}
          reactlytics={{ disable: true }}
        >
          {buttonText}
        </Button>
      );
    }

    return (
      <Link
        className={clsx(
          'btn primary btn-primary flex-justify-center dls-white tw-w-full tw-max-w-300',
          styles.waitListButton
        )}
        href={url}
        isInline
        onClick={clickHandler}
        reactlytics={{ disable: true }}
        target="_blank"
      >
        {buttonText}
      </Link>
    );
  };

  return (
    <div className="margin-3 pad-3-tb">
      {instructions && (
        <div className="margin-2-b tw-flex">
          <div>
            <Icon className="tw-mr-2" size="md" />
          </div>
          <div>
            <h2>
              {prefix && (
                <span className="tw-text-gray-100">{`${prefix} `}</span>
              )}
              <span className="tw-heading-xs-bold tw-text-gray-100">
                {title}
              </span>
            </h2>
            <div className="tw-pr-4 tw-text-sm tw-text-gray-200">
              {instructions}
            </div>
          </div>
        </div>
      )}
      {displayEntryPoint()}
    </div>
  );
}
